import { type FC } from 'react';

import { useFundEsgQuery } from '@/api/esg/esgApi';
import { Column, Row } from '@/components/Grid';
import { ConnectPopover } from '@/components/Pretzel/ConnectPopover';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';

import { EsgSolutionsGraph } from '../components/EsgSolutionsGraph';
import {
  InvestmentSolutionsRow,
  InvestmentSolutionsTable,
} from '../components/InvestmentSolutionsTable';
import { LargeText } from '../components/LargeText';

interface Props {
  isin: string;
}

export const EsgSolutions: FC<Props> = ({ isin }) => {
  const texts = useTexts();
  const esgFundQuery = useFundEsgQuery({ isin });

  const hasSustainableDebtInstruments =
    esgFundQuery.data!.greenBond ||
    esgFundQuery.data!.socialBond ||
    esgFundQuery.data!.sustainabilityLinkedBond ||
    esgFundQuery.data!.sustainabilityBond;

  return (
    <WithGap>
      <WithGap gap="8" direction="row">
        <h2>{texts.pages.esg.solutions.title}</h2>

        {texts.pages.esg.solutions.helpDescription && (
          <ConnectPopover
            id="fund-esg-carbon-intensities"
            description={texts.pages.esg.solutions.helpDescription}
          />
        )}
      </WithGap>

      <Row>
        <Column $span={6}>
          <WithGap gap="16">
            <LargeText>
              {esgFundQuery.data!.solutionCompanies!.kpi.value} %
            </LargeText>

            <div className="font-body-demibold">
              {texts.pages.fundEsg.solutions.subtitle}
            </div>

            <p className="font-body">{texts.pages.esg.solutions.description}</p>

            <WithGap gap="48">
              <InvestmentSolutionsTable>
                {esgFundQuery.data!.solutionCompaniesCircularEconomy && (
                  <InvestmentSolutionsRow
                    title={texts.pages.fundEsg.solutions.companyCircularEconomy}
                    value={
                      esgFundQuery.data!.solutionCompaniesCircularEconomy.kpi
                        .value
                    }
                  />
                )}

                {esgFundQuery.data!.solutionCompaniesSmartCities && (
                  <InvestmentSolutionsRow
                    title={texts.pages.fundEsg.solutions.companySmartCities}
                    value={
                      esgFundQuery.data!.solutionCompaniesSmartCities.kpi.value
                    }
                  />
                )}

                {esgFundQuery.data!.solutionCompaniesEqualOpportunities && (
                  <InvestmentSolutionsRow
                    title={
                      texts.pages.fundEsg.solutions.companyEqualOpportunities
                    }
                    value={
                      esgFundQuery.data!.solutionCompaniesEqualOpportunities.kpi
                        .value
                    }
                  />
                )}

                {esgFundQuery.data!.solutionCompaniesRenewableEnergy && (
                  <InvestmentSolutionsRow
                    title={texts.pages.fundEsg.solutions.companyRenewableEnergy}
                    value={
                      esgFundQuery.data!.solutionCompaniesRenewableEnergy.kpi
                        .value
                    }
                  />
                )}

                {esgFundQuery.data!.solutionCompaniesCrossCutting && (
                  <InvestmentSolutionsRow
                    title={texts.pages.fundEsg.solutions.companyCrossCutting}
                    value={
                      esgFundQuery.data!.solutionCompaniesCrossCutting.kpi.value
                    }
                  />
                )}
              </InvestmentSolutionsTable>

              {hasSustainableDebtInstruments && (
                <div>
                  <p className="font-body-demibold">
                    {texts.pages.fundEsg.solutions.sustainableDebt}
                  </p>

                  <InvestmentSolutionsTable>
                    {esgFundQuery.data!.greenBond && (
                      <InvestmentSolutionsRow
                        title={texts.pages.fundEsg.solutions.greenBond}
                        value={esgFundQuery.data!.greenBond.kpi.value}
                      />
                    )}

                    {esgFundQuery.data!.socialBond && (
                      <InvestmentSolutionsRow
                        title={texts.pages.fundEsg.solutions.socialBonds}
                        value={esgFundQuery.data!.socialBond.kpi.value}
                      />
                    )}

                    {esgFundQuery.data!.sustainabilityBond && (
                      <InvestmentSolutionsRow
                        title={texts.pages.fundEsg.solutions.sustainabilityBond}
                        value={esgFundQuery.data!.sustainabilityBond.kpi.value}
                      />
                    )}

                    {esgFundQuery.data!.sustainabilityLinkedBond && (
                      <InvestmentSolutionsRow
                        title={
                          texts.pages.fundEsg.solutions.sustainabilityLinkedBond
                        }
                        value={
                          esgFundQuery.data!.sustainabilityLinkedBond.kpi.value
                        }
                      />
                    )}
                  </InvestmentSolutionsTable>
                </div>
              )}
            </WithGap>
          </WithGap>
        </Column>

        <Column $span={6}>
          <EsgSolutionsGraph
            solutionCompaniesEqualOpportunities={
              esgFundQuery.data!.solutionCompaniesEqualOpportunities
            }
            solutionCompaniesRenewableEnergy={
              esgFundQuery.data!.solutionCompaniesRenewableEnergy
            }
            solutionCompaniesCircularEconomy={
              esgFundQuery.data!.solutionCompaniesCircularEconomy
            }
            solutionCompaniesCrossCutting={
              esgFundQuery.data!.solutionCompaniesCrossCutting
            }
            solutionCompaniesSmartCities={
              esgFundQuery.data!.solutionCompaniesSmartCities
            }
          />
        </Column>
      </Row>
    </WithGap>
  );
};
